<template>
  <div class="protocol-config">
    <!-- 搜索工具栏 -->
    <div class="toolbar">
      <div class="search-group">
        <label>条约标题：</label>
        <el-input 
          v-model="searchParams.title" 
          placeholder="请输入条约标题" 
          style="width: 150px; margin-right: 10px;"
          clearable
        />
        
        <el-select 
          v-model="searchParams.type" 
          placeholder="按协议类型"
          style="width: 120px; margin-right: 10px;"
          clearable
        >
          <el-option label="按产品类型" :value="1" />
          <el-option label="按App类型" :value="2" />
        </el-select>

        <el-select 
          v-model="searchParams.product_id" 
          placeholder="请选择产品"
          v-show="searchParams.type === 1"
          style="width: 140px; margin-right: 10px;"
          clearable
          filterable
        >
          <el-option
            v-for="item in productList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>

        <el-select 
          v-model="searchParams.app_id" 
          placeholder="请选择平台"
          v-show="searchParams.type === 2"
          style="width: 140px; margin-right: 10px;"
          clearable
          filterable
        >
          <el-option
            v-for="item in appList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>

        <el-checkbox v-model="searchParams.onlyPublished" style="margin-right: 10px;">
          按状态为发布
        </el-checkbox>

        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">全部</el-button>
      </div>
      
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd" class="add-btn">
        新增条约
      </el-button>
    </div>

    <!-- 数据表格 -->
    <el-table 
      :data="tableData" 
      :height="tableHeight"
      v-loading="loading"
      border
      stripe
      :header-cell-style="{ backgroundColor: '#f5f7fa', color: '#606266' }"
      style="width: 100%;"
    >
      <el-table-column type="index" label="序号" width="60" align="center" fixed="left" />
      
      <el-table-column prop="product_name" label="产品名称" min-width="140" align="center" show-overflow-tooltip />
      
      <el-table-column prop="price_name" label="价格方案名称" min-width="140" align="center" show-overflow-tooltip />
      
      <el-table-column prop="app_name" label="平台名称" min-width="140" align="center" show-overflow-tooltip />
      
      <el-table-column prop="title" label="标题" min-width="180" align="center" show-overflow-tooltip />
      
      <el-table-column prop="version" label="版本" min-width="100" align="center" />
      
      <el-table-column prop="disabled" label="状态" min-width="80" align="center">
        <template #default="{ row }">
          <el-tag :type="row.disabled ? 'danger' : 'success'">
            {{ row.disabled ? '禁用' : '发布' }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="required" label="是否需要签署" min-width="120" align="center">
        <template #default="{ row }">
          <el-tag :type="row.required ? 'success' : 'info'">
            {{ row.required ? '需要' : '不需要' }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="created_at" label="创建时间" min-width="180" align="center">
        <template #default="{ row }">
          {{ formatDate(row.created_at) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="updated_at" label="更新时间" min-width="180" align="center">
        <template #default="{ row }">
          {{ formatDate(row.updated_at) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="author" label="修改人" min-width="120" align="center" show-overflow-tooltip />
      
      <el-table-column label="操作" width="200" align="center">
        <template #default="{ row }">
          <el-button type="text" size="small" @click="handleView(row)">查看</el-button>
          <el-button type="text" size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button 
            type="text" 
            size="small" 
            :disabled="!row.disabled"
            @click="handleDelete(row)"
          >
            删除
          </el-button>
          <el-button 
            type="text" 
            size="small" 
            @click="handleToggleStatus(row)"
          >
            {{ row.disabled ? '发布' : '禁用' }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="pagination.rows"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
      />
    </div>

    <!-- 协议编辑对话框 -->
    <protocol-edit-dialog
      v-if="showEditDialog"
      :visible.sync="showEditDialog"
      :protocol-data="currentProtocol"
      :is-view="isViewMode"
      @save="handleSaveProtocol"
      @cancel="handleCancelEdit"
    />

    <!-- 协议内容查看对话框 -->
    <el-dialog
      title="查看协议"
      :visible.sync="showViewDialog"
      width="60%"
      :close-on-click-modal="false"
    >
      <div v-html="viewContent" style="max-height: 400px; overflow-y: auto;"></div>
      <div slot="footer">
        <el-button @click="showViewDialog = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import ProtocolEditDialog from './components/ProtocolEditDialog.vue'
import { 
  getProtocolList, 
  deleteProtocol, 
  toggleProtocolStatus,
  getProductList,
  getAppList 
} from '@/api/product'

export default {
  name: 'ProtocolConfig',
  components: {
    ProtocolEditDialog
  },
  data() {
    return {
      loading: false,
      tableData: [],
      searchParams: {
        title: '',
        type: null,
        product_id: null,
        app_id: null,
        onlyPublished: false
      },
      pagination: {
        page: 1,
        rows: 15,
        total: 0
      },
      productList: [],
      appList: [],
      showEditDialog: false,
      showViewDialog: false,
      currentProtocol: null,
      isViewMode: false,
      viewContent: '',
      tableHeight: 600 // 设置一个默认的合理高度
    }
  },
  mounted() {
    // 加载数据
    this.loadData()
    this.loadProductList()
    this.loadAppList()
    
    // 延迟计算表格高度，确保DOM完全渲染
    setTimeout(() => {
      this.calculateTableHeight()
    }, 100)
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.calculateTableHeight)
  },
  created() {
    // 在组件创建时就计算一次高度
    this.calculateTableHeight()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    // 计算表格高度
    calculateTableHeight() {
      this.$nextTick(() => {
        const windowHeight = window.innerHeight
        // 简化计算，直接减去固定高度
        this.tableHeight = Math.max(windowHeight - 180, 400)
        
        // 延迟刷新表格布局，确保表头同步
        setTimeout(() => {
          this.doLayout()
        }, 50)
      })
    },

    // 刷新表格布局
    doLayout() {
      this.$nextTick(() => {
        const table = this.$el.querySelector('.el-table')
        if (table && table.__vue__ && table.__vue__.doLayout) {
          table.__vue__.doLayout()
        }
      })
    },

    // 加载数据
    async loadData() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          rows: this.pagination.rows
        }

        // 添加搜索条件
        if (this.searchParams.title) {
          params.title = this.searchParams.title
        }
        if (this.searchParams.onlyPublished) {
          params.disabled = false
        }
        if (this.searchParams.type === 1 && this.searchParams.product_id) {
          params.product_id = this.searchParams.product_id
        }
        if (this.searchParams.type === 2 && this.searchParams.app_id) {
          params.app_id = this.searchParams.app_id
        }

        const response = await getProtocolList(params)
        if (response && response.err_code === 0) {
          this.tableData = response.rows || []
          this.pagination.total = response.total || 0
          console.log('协议数据加载成功:', this.tableData.length, '条记录')
          
          // 数据加载完成后刷新表格布局
          this.$nextTick(() => {
            this.doLayout()
          })
        } else {
          this.$message.error('获取数据失败')
          console.error('接口返回错误:', response)
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('网络错误，请重试')
      } finally {
        this.loading = false
      }
    },

    // 加载产品列表
    async loadProductList() {
      try {
        const response = await getProductList()
        if (response && response.err_code === 0) {
          this.productList = response.rows || []
        }
      } catch (error) {
        console.error('加载产品列表失败:', error)
      }
    },

    // 加载应用列表
    async loadAppList() {
      try {
        const response = await getAppList()
        if (response && response.err_code === 0) {
          this.appList = response.rows || []
        }
      } catch (error) {
        console.error('加载应用列表失败:', error)
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.loadData()
    },

    // 重置搜索
    handleReset() {
      this.searchParams = {
        title: '',
        type: null,
        product_id: null,
        app_id: null,
        onlyPublished: false
      }
      this.pagination.page = 1
      this.loadData()
    },

    // 新增
    handleAdd() {
      this.currentProtocol = null
      this.isViewMode = false
      this.showEditDialog = true
    },

    // 查看
    handleView(row) {
      this.viewContent = row.content || ''
      this.showViewDialog = true
    },

    // 编辑
    handleEdit(row) {
      this.currentProtocol = { ...row }
      this.isViewMode = false
      this.showEditDialog = true
    },

    // 删除
    async handleDelete(row) {
      try {
        await this.$confirm('您确认想要删除该条约吗？', '确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await deleteProtocol({ id: row.id })
        if (response && response.err_code === 0) {
          this.$message.success('删除条约成功')
          this.loadData()
        } else {
          this.$message.error(response?.err_msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error('网络错误，请重试')
        }
      }
    },

    // 切换状态
    async handleToggleStatus(row) {
      const action = row.disabled ? '发布' : '禁用'
      try {
        await this.$confirm(`您确认想要${action}该条约吗？`, '确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const response = await toggleProtocolStatus({ 
          id: row.id, 
          disabled: !row.disabled 
        })
        if (response && response.err_code === 0) {
          this.$message.success(`${action}条约成功`)
          this.loadData()
        } else {
          this.$message.error(response?.err_msg || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error(`${action}失败:`, error)
          this.$message.error('网络错误，请重试')
        }
      }
    },

    // 保存协议
    handleSaveProtocol() {
      this.showEditDialog = false
      this.loadData()
    },

    // 取消编辑
    handleCancelEdit() {
      this.showEditDialog = false
      this.currentProtocol = null
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.rows = val
      this.pagination.page = 1
      this.loadData()
    },

    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadData()
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.protocol-config {
  padding: 20px;
  height: 100vh;
  box-sizing: border-box;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 10px 0;
}

.search-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-group label {
  white-space: nowrap;
}

.add-btn {
  margin-left: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

/* 表格样式优化 */
.protocol-config ::v-deep .el-table {
  border: 1px solid #ebeef5;
}

.protocol-config ::v-deep .el-table__body-wrapper {
  overflow-x: auto;
  overflow-y: auto;
}

.protocol-config ::v-deep .el-table__header-wrapper {
  overflow: hidden;
}

/* 解决表头滚动同步问题 */
.protocol-config ::v-deep .el-table__header {
  width: 100% !important;
}

.protocol-config ::v-deep .el-table__body {
  width: 100% !important;
}

/* 固定列样式优化 */
.protocol-config ::v-deep .el-table__fixed {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.12);
}

.protocol-config ::v-deep .el-table__fixed-right {
  box-shadow: -1px 0 8px rgba(0, 0, 0, 0.12);
}

/* 表头样式 */
.protocol-config ::v-deep .el-table th {
  background-color: #f5f7fa !important;
  color: #606266 !important;
  font-weight: 500;
}

/* 表格内容溢出处理 */
.protocol-config ::v-deep .el-table .cell {
  padding: 0 8px;
  word-break: break-all;
}

/* 确保表格容器宽度 */
.protocol-config ::v-deep .el-table {
  width: 100% !important;
  table-layout: fixed;
}
</style>